// D009 — lightweight UTM attribution.
// Capture first-touch landing parameters in localStorage and attach them to the
// player account once after authentication. Opaque, bounded, lowercase marketing
// parameters only — no referrer URLs, no identifiers; no gameplay behavior
// depends on this data and failures are silent by design.
import { API_BASE } from '../config';

const STORAGE_KEY = 'tw3002_attribution_v1';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

export type UtmParam = (typeof UTM_PARAMS)[number];

/** API field names (the utm_ prefix is stripped for storage and transport). */
export type AttributionField = 'source' | 'medium' | 'campaign' | 'content';

export interface AttributionPayload {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  capturedAt: string;
  /** Set once the server has accepted the value; prevents re-sending. */
  attached?: boolean;
}

const MAX_LENGTH = 64;
const VALUE_PATTERN = /^[a-z0-9_-]+$/;

function sanitizeValue(raw: string | null): string | undefined {
  if (!raw) return undefined;
  const normalized = raw.trim().toLowerCase().slice(0, MAX_LENGTH);
  return VALUE_PATTERN.test(normalized) ? normalized : undefined;
}

function readStored(): AttributionPayload | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AttributionPayload;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Returns the search string with any utm_* parameters removed (other params preserved). */
export function stripUtmParams(search: string): string {
  const params = new URLSearchParams(search);
  let touched = false;
  for (const param of UTM_PARAMS) {
    if (params.has(param)) {
      params.delete(param);
      touched = true;
    }
  }
  if (!touched) return search;
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * First-touch capture: reads utm_* parameters from the current URL, stores them
 * once (an existing stored payload always wins), and strips the parameters from
 * the address bar so they don't leak into copied or screenshotted URLs.
 * No-op when the URL carries no utm parameters.
 */
export function captureLandingAttribution(
  search: string = window.location.search,
  applyStrippedSearch: (stripped: string) => void = (stripped) => {
    if (!stripped) return;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${stripped}${window.location.hash}`);
  },
): AttributionPayload | null {
  const params = new URLSearchParams(search);
  const captured: Partial<Record<AttributionField, string>> = {};
  let sawUtmKey = false;
  for (const param of UTM_PARAMS) {
    if (params.has(param)) sawUtmKey = true;
    const value = sanitizeValue(params.get(param));
    if (value) captured[param.slice('utm_'.length) as AttributionField] = value;
  }
  if (sawUtmKey) applyStrippedSearch(stripUtmParams(search));
  if (Object.keys(captured).length === 0) return null;

  const existing = readStored();
  if (existing) return existing; // first touch wins

  const payload: AttributionPayload = { ...captured, capturedAt: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable (private mode etc.): attribution is best-effort.
  }
  return payload;
}

export function getStoredAttribution(): AttributionPayload | null {
  return readStored();
}

export function hasUnattachedAttribution(): boolean {
  const stored = readStored();
  return !!stored && !stored.attached;
}

export function markAttributionAttached(): void {
  const stored = readStored();
  if (!stored || stored.attached) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...stored, attached: true }));
  } catch {
    // Storage unavailable: the value simply re-sends next session; the server
    // treats repeats as idempotent already_set.
  }
}

export type AttributionPostResult = 'sent' | 'none' | 'failed';

/**
 * Fire-and-forget attach: POSTs the stored, unattached payload to
 * /api/player/attribution with the caller's auth headers. Never throws.
 */
export async function postAttribution(getHeaders: () => Record<string, string>): Promise<AttributionPostResult> {
  const payload = getStoredAttribution();
  if (!payload || payload.attached) return 'none';
  const { capturedAt: _capturedAt, attached: _attached, ...utm } = payload;
  try {
    const res = await fetch(`${API_BASE}/api/player/attribution`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(utm),
    });
    if (!res.ok) return 'failed';
    markAttributionAttached();
    return 'sent';
  } catch {
    return 'failed';
  }
}
